/**
 * Piano di pagamento a rate di un itinerario.
 *
 * Il piano NON fa parte di ItineraryData e non viaggia dentro save_itinerary: quella RPC
 * gira in autosave a ogni modifica del builder, e riscriverebbe a ogni battuta di tasto
 * righe che contengono stato del server (paid_at, payment_id). Ha una RPC dedicata.
 */

/**
 * @deprecated Il piano non ha piu' modalita': nasce con una rata sola — che vale come
 * pagamento unico — e si rateizza aggiungendo righe. Il tipo resta perche' `mode` esiste
 * ancora a schema e la RPC lo restituisce, ma da qui in avanti vale sempre
 * `'installments'`; `'single'` compare solo su piani salvati prima del 26/08/2026.
 */
export type PaymentPlanMode = 'single' | 'installments';

export type InstallmentStatus = 'pending' | 'paid' | 'cancelled';

/** Campi che il TD modifica nell'editor. */
export interface IInstallmentDraft {
    /** Assente sulle rate appena aggiunte, che il DB deve ancora creare. */
    id?: string;
    label: string;
    /** Importo in euro. E' la fonte di verita': la percentuale e' solo derivata. */
    amount: number;
    /** ISO date, YYYY-MM-DD. */
    due_date: string;
    /** Se true il cliente riceve una mail il giorno della scadenza. */
    notify_on_due: boolean;
    /** Giorni PRIMA della scadenza, sempre negativi. Es. [-7, -1]. */
    reminder_offsets: number[];
}

/** Una rata come torna dal database, con lo stato che il frontend non decide. */
export interface IInstallment extends IInstallmentDraft {
    id: string;
    seq: number;
    percentage: number | null;
    status: InstallmentStatus;
    paid_at: string | null;
    paid_amount: number | null;
    payment_method: string | null;
    payment_id: string | null;
    /**
     * Esiste un tentativo di pagamento non ancora risolto su questa rata: il cliente ha
     * pagato con un metodo ad addebito differito (SEPA) e Stripe non ha ancora confermato.
     * La rata resta `pending` ma non va ne' pagata di nuovo ne' sollecitata.
     */
    payment_processing: boolean;
    /** Derivato dal DB sulla data italiana, non ricalcolarlo nel client. */
    is_overdue: boolean;
    last_notified_at: string | null;
}

export interface IPaymentPlan {
    id: string;
    itinerary_id: string;
    /**
     * @deprecated Residuo di quando il piano aveva due modalita'. Vale sempre
     * `'installments'`: un piano da una rata sola E' il pagamento unico. La RPC lo
     * restituisce ancora, ma nessuna UI deve ramificare su questo campo.
     */
    mode: PaymentPlanMode;
    currency: string;
    /** Totale dell'itinerario al momento in cui il piano e' stato salvato. */
    total_amount: number;
    /** Totale dell'itinerario adesso. */
    itinerary_total: number;
    /** true quando i due sopra divergono: il preventivo e' cambiato dopo il piano. */
    total_mismatch: boolean;
    updated_at: string;
    installments: IInstallment[];
}

export interface SavePaymentPlanPayload {
    itinerary_id: string;
    currency?: string;
    installments: IInstallmentDraft[];
}

/**
 * Esito di un sollecito inviato a mano.
 *
 * `template` dice quale mail e' effettivamente partita: la scelta la fa il database
 * sulla data italiana, quindi non e' anticipabile lato client.
 */
export interface SendInstallmentReminderResult {
    reminder_id: string;
    mail_id: string;
    template: 'installment_overdue' | 'installment_due' | 'installment_reminder';
    to_email: string;
    days_overdue: number;
}

/** Offset proposti dalla UI. Il campo accetta comunque valori liberi. */
export const DEFAULT_REMINDER_OFFSETS = [-30, -14, -7, -3, -1] as const;

/** Offset applicati a una rata appena creata. */
export const NEW_INSTALLMENT_REMINDERS = [-7, -1];

export const isEditableInstallment = (installment: Pick<IInstallment, 'status'>): boolean =>
    installment.status !== 'paid';
